import { useTranslation } from "react-i18next";

interface AiLoaderProps {
  text?: string;
  className?: string;
}

export function AiLoader({ text, className = "" }: AiLoaderProps) {
  const { t } = useTranslation();
  const label = text || t("loading.analyzing") || "Analyzing data…";

  return (
    <div className={`flex flex-col items-center justify-center gap-4 py-12 ${className}`}>
      {/* Robot SVG */}
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 80 80" className="h-20 w-20" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Antenna */}
          <line x1="40" y1="8" x2="40" y2="18" className="stroke-primary" strokeWidth="2" strokeLinecap="round">
            <animate attributeName="y1" values="8;5;8" dur="1.5s" repeatCount="indefinite" />
          </line>
          <circle cx="40" cy="6" r="3" className="fill-primary">
            <animate attributeName="cy" values="6;3;6" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
          </circle>

          {/* Head */}
          <rect x="22" y="18" width="36" height="28" rx="8" className="fill-primary/10 stroke-primary" strokeWidth="2" />

          {/* Eyes */}
          <circle cx="33" cy="30" r="4" className="fill-primary">
            <animate attributeName="r" values="4;3;4" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="47" cy="30" r="4" className="fill-primary">
            <animate attributeName="r" values="4;3;4" dur="2s" repeatCount="indefinite" begin="0.1s" />
          </circle>
          {/* Eye glints */}
          <circle cx="34.5" cy="28.5" r="1.2" className="fill-background" opacity="0.8" />
          <circle cx="48.5" cy="28.5" r="1.2" className="fill-background" opacity="0.8" />

          {/* Mouth - thinking expression */}
          <path d="M33 39 Q37 42 40 39 Q43 36 47 39" className="stroke-primary/60" strokeWidth="1.5" fill="none" strokeLinecap="round">
            <animate attributeName="d" values="M33 39 Q37 42 40 39 Q43 36 47 39;M33 40 Q40 43 47 40;M33 39 Q37 42 40 39 Q43 36 47 39" dur="3s" repeatCount="indefinite" />
          </path>

          {/* Body */}
          <rect x="26" y="48" width="28" height="16" rx="5" className="fill-primary/10 stroke-primary" strokeWidth="2" />

          {/* Body detail - chest light */}
          <circle cx="40" cy="55" r="2.5" className="fill-primary/60">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
          </circle>

          {/* Left arm - searching */}
          <g>
            <animateTransform attributeName="transform" type="rotate" values="-10 22 52;10 22 52;-10 22 52" dur="1.8s" repeatCount="indefinite" />
            <line x1="22" y1="52" x2="12" y2="58" className="stroke-primary" strokeWidth="2" strokeLinecap="round" />
            <circle cx="10" cy="59" r="3" className="fill-primary/20 stroke-primary" strokeWidth="1.5" />
          </g>

          {/* Right arm - waving/thinking */}
          <g>
            <animateTransform attributeName="transform" type="rotate" values="10 58 52;-10 58 52;10 58 52" dur="1.8s" repeatCount="indefinite" begin="0.3s" />
            <line x1="58" y1="52" x2="68" y2="48" className="stroke-primary" strokeWidth="2" strokeLinecap="round" />
            <circle cx="70" cy="47" r="3" className="fill-primary/20 stroke-primary" strokeWidth="1.5" />
          </g>

          {/* Feet */}
          <rect x="28" y="64" width="8" height="4" rx="2" className="fill-primary/30" />
          <rect x="44" y="64" width="8" height="4" rx="2" className="fill-primary/30" />

          {/* Floating thought particles */}
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={62 + i * 5} cy={20 - i * 5} r={1.5 - i * 0.3} className="fill-primary/40">
              <animate attributeName="opacity" values="0;1;0" dur={`${1.5 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} />
              <animate attributeName="cy" values={`${20 - i * 5};${16 - i * 5};${20 - i * 5}`} dur={`${1.5 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} />
            </circle>
          ))}
        </svg>
      </div>

      {/* Text */}
      <p className="text-xs font-medium text-muted-foreground tracking-wide animate-pulse">
        {label}
      </p>
    </div>
  );
}